<template>
    <div class="current-subscription" :class="[subscriptionDetailsObject.subscriptionStyle, variant]">
        <div v-if="loading" class="skeleton-wrap">
            <b-skeleton width="45%"/>
            <ul>
                <li><b-skeleton width="40%"/></li>
                <li><b-skeleton width="50%"/></li>
                <li><b-skeleton width="45%"/></li>
            </ul>
            <b-skeleton type="button"/>
        </div>
        <div v-if="!loading" class="details-wrap">
            <div class="purchase-summary">
                <h3>{{ subscriptionDetailsObject.title }}</h3>
                <ul class="amount-lines">
                    <li>
                        <div class="line-desc">
                            <font-awesome-icon :icon="subscriptionDetailsObject.starIconClass" class="mr-1" :class="subscriptionDetailsObject.accountTextClass" />
                            <span :class="subscriptionDetailsObject.accountTextClass">{{ subscriptionDetailsObject.accountText }}</span>
                        </div>
                        <div class="price">
                            <span v-if="subscriptionDetailsObject.subscriptionCost != 'free'">€</span>{{ subscriptionDetailsObject.subscriptionCost }}
                        </div>
                    </li>
                    <li>
                        <div class="line-desc">
                            <font-awesome-icon icon="fa-solid fa-user" class="text-unassigned mr-1" />
                            <span class="text-unassigned">
                                <span>{{ subscriptionDetailsObject.addMembersNumber }}</span> <translate>additional team members</translate>
                            </span>
                            <font-awesome-icon :id="'tt_subscriptions_teammembers-' + variant" class="text-info tooltip-icon tooltip-icon--question ml-1" v-b-tooltip.focus icon="fa-solid fa-circle-question" />
                            <b-tooltip :target="'tt_subscriptions_teammembers-' + variant" triggers="hover">
                                <div class="tooltip-text">
                                    {{ $gettext('Only you have access to your company. You need to upgrade to premium to invite team members.') }}
                                </div>
                            </b-tooltip>
                        </div>
                        <div class="price">
                            <span v-if="subscriptionDetailsObject.addMembersCost != '-'">€</span>{{ subscriptionDetailsObject.addMembersCost }}
                        </div>
                    </li>
                    <li v-if="selectedCycle>=12">
                        <div>
                            <font-awesome-icon icon="fa-solid fa-gift" class="text-primary mr-1" />
                            <span class="text-primary">{{ subscriptionDiscount.short_line_description }}</span>
                        </div>
                        <div class="price price--discount">-€{{ subscriptionDetailsObject.discount }}</div>
                    </li>
                    <li>
                        <div v-if="buttonVariant === 'cycle'" class="billing-period-wrap">
                            <GekkoTablist 
                                v-model="selectedCycle"
                                :options="subscriptionOptions"
                                @change="onSubscriptionSelected"
                            />
                        </div>
                        <ActionButtonChange v-if="buttonVariant === 'dropdown'"/>
                        <ActionButtonUndo v-if="buttonVariant === 'cancelled'"/>
                        <ActionButtonUpgrade v-if="buttonVariant === 'upgrade'"/>
                    </li>
                </ul>
            </div>
            <div class="billing-total">
                <div class="amount-wrap">
                    <div>
                        <div v-if="showDate" class="subtitle" :class="subscriptionDetailsObject.dateMessageClass">
                            <span class="subtitle-text">{{ subscriptionDetailsObject.dateMessage }}</span> {{ subscriptionDetailsObject.endDate | formatDate}}
                            <a v-if="variant==='old'" @click="paymentDetailsUrl()" class="act act-primary" v-translate>(Details)</a>
                        </div>
                    </div>
                    <div class="bignumber">
                        <span v-if="subscriptionDetailsObject.totalCost != 'free'">€</span>{{ subscriptionDetailsObject.totalCost }}
                    </div>
                </div>
                
            </div>
        </div>
    </div>
</template>

<script>
    import DiscountService from "@/store/services/discount.service";
    import GekkoTablist from "@/components/GekkoTablist";

    import ActionButtonChange from "@/components/subscriptions/decorator/ActionButtonChange";
    import ActionButtonUndo from "@/components/subscriptions/decorator/ActionButtonUndo";
    import ActionButtonUpgrade from "@/components/subscriptions/decorator/ActionButtonUpgrade";

    const COST_PER_MONTH = 12;
    const PRICE_PER_USER = 5;

    export default {
        name: "SubscriptionDetails",
        components: { ActionButtonChange, ActionButtonUndo, ActionButtonUpgrade, GekkoTablist },
        props: {
            variant: String,
            buttonVariant: String,
            enabled: {
                type: Boolean,
                default: true
            },
        },
        data() {
            return {
                subscriptionDetailsObject: {},
                discount_object: {},
                loading: true,
                selectedCycle:1,
                isPremium: this.$store.getters["getIsPremium"],
                subscriptionOptions: [
                    {content: this.$gettext("Monthly"), value: 1},
                    {content: this.$gettext("Yearly") + ' <span class="yearly-offer">15% off!</span>', value: 12}
                ],
                showDate: false,
            }
        },
        watch: {
            "$store.state.subscriptionStore.subscription": {
                handler: function (val) {
                    if (this.variant === "old") {
                        this.initCurrentPlan(val);
                    } else {
                        this.initNewPlan(val);
                    }
                },
                deep: true
            }
        },
        created(){
            DiscountService.list().then((response) => {
                if (response.data.length > 0)
                    this.subscriptionDiscount = response.data[0]; // the highest priority will always be the first element in the array
                else {
                    // if not subscription discounts are found, hardcode the yearly subscription
                    this.subscriptionDiscount = {
                        amount: "15.00",
                        short_line_description: this.$gettext("Yearly discount (15%)")
                    }
                }

                let discountTriggerText;
                if (this.subscriptionDiscount.benefit_type === "amount") {
                    discountTriggerText = "€" + this.subscriptionDiscount.amount + " " + this.$gettext("off!");
                } else {
                    discountTriggerText = parseInt(this.subscriptionDiscount.amount) + "% " + this.$gettext("off!");
                }
                this.subscriptionOptions[1].content = `${ this.$gettext("Yearly") } <span class="yearly-offer">${ discountTriggerText }</span>`;
                if (this.variant !== "old") {
                    this.initNewPlan(this.$store.getters['subscriptionStore/getSubscription']);
                }
            });
        },
        methods: {
            initCurrentPlan(subscription) {
                if (subscription.is_valid) {  //premium
                    this.subscriptionDiscount = {
                        amount: "15.00",
                        short_line_description: this.$gettext("Yearly discount (15%)")
                    };
                    this.selectedCycle = subscription.renewal_months;
                    this.onSubscriptionSelected(subscription.renewal_months);
                } else {  //basic
                    this.initBasicSubscription();
                }
                if (!this.enabled) {
                    this.subscriptionDetailsObject.dateMessage = this.$gettext("Until");
                    this.subscriptionDetailsObject.subscriptionStyle = "prev-subscription";
                }
                this.loading = false;
            },
            initBasicSubscription() {
                this.showDate = false;
                this.subscriptionDetailsObject = {
                    title: this.$gettext("Gekko Basic"),
                    endDate: new Date(),
                    discount: 0,
                    subscriptionCost: this.$gettext("free"),
                    addMembersNumber: 0,
                    addMembersCost: "-",
                    totalCost: this.$gettext("free"),
                    dateMessage: this.$gettext("Next payment on"),
                    starIconClass: "fa-solid fa-star-half",
                    accountTextClass: "text-unassigned",
                    accountText: this.$gettext("Basic account"),
                    subscriptionStyle: null
                };
            },
            initNewPlan() {
                if (this.subscriptionDiscount) {
                    if (this.variant === "downgrade") {
                        this.initBasicSubscription();
                    } else {
                        if(this.$route.query.selected === 'year'){
                            this.selectedCycle = 12;
                        }
                        this.onSubscriptionSelected(this.selectedCycle);
                    }
                    this.loading = false;
                }
            },
            onSubscriptionSelected(value) {
                var subscription = this.$store.getters['subscriptionStore/getSubscription'];
                this.showDate = true;
                var title = this.$gettext("Monthly subscription");
                if (value >= 12) {
                    title = this.$gettext("Yearly subscription");
                }
                var date = new Date(subscription.get_end_date);
                if(date < new Date()){
                    date = new Date();
                }
                if(this.variant !== 'old') {
                    date.setMonth(date.getMonth() + value);
                }

                this.subscriptionDetailsObject = {
                    title: title,
                    endDate: date,
                    starIconClass: "fa-solid fa-star",
                    accountTextClass: "text-favorite",
                    accountText: this.$gettext("Premium account"),
                    subscriptionCost: COST_PER_MONTH * value,
                    addMembersNumber: subscription.number_of_extra_connected_and_pending_users,
                    addMembersCost: PRICE_PER_USER * value * subscription.number_of_extra_connected_and_pending_users,
                    dateMessage: this.$gettext("Next payment on"),
                };
                if(subscription.cancelled){
                    this.subscriptionDetailsObject.dateMessageClass = "text-center text-danger";
                    this.subscriptionDetailsObject.dateMessage = this.$gettext("Until");
                }
                if (this.subscriptionDiscount.benefit_type === "amount") {
                    this.subscriptionDetailsObject.discount = Math.round(this.subscriptionDiscount.amount);
                }else {
                    let percentage = this.subscriptionDiscount.amount * 0.01; // from % to a percentage
                    this.subscriptionDetailsObject.discount = Math.round(percentage * (value * COST_PER_MONTH + this.subscriptionDetailsObject.addMembersCost));
                }

                this.subscriptionDetailsObject.totalCost = this.subscriptionDetailsObject.subscriptionCost + this.subscriptionDetailsObject.addMembersCost;
                if (value >= 12) {
                    this.subscriptionDetailsObject.totalCost -= this.subscriptionDetailsObject.discount;
                }

                this.$emit('changedCycle', value);
            },
            paymentDetailsUrl() {
                this.$router.push({name: "PaymentDetails"});
            }
        },
    }
</script>

<style scoped lang="scss">
    .current-subscription {
        position: relative;
        border-radius: $radiusLg;
        height: 100%;
        h3 { margin: 0; }
        .skeleton-wrap { padding: 1rem; }
        &.prev-subscription {
            background-color: #f6f6f6;
            overflow: hidden;
        }
        ul {
            list-style: none;
            margin: 0; padding: 0;
        }
        .details-wrap {
            display: flex;
            flex-direction: column;
            height: 100%;
            justify-content: space-between;
            .subtitle {
                color: $colorTextLight;
                .subtitle-text {
                    color: $colorTextLight;
                }
            }
            .purchase-summary {
                padding: 1rem;
                .amount-lines {
                    padding: 1rem 0;
                    padding-bottom: 0;
                    li {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 0;
                        font-size: 16px;
                        .price { font-size: 22px; opacity: 0.7; }
                        .svg-inline--fa { width: 20px; }

                        .billing-period-wrap {
                            display: flex;
                            align-items: center;
                            flex-wrap: wrap;
                            width: 100%;
                            padding-top: 0.5rem;
                            gap: 0.5rem;
                            .billing-period-label {
                                display: inline-block;
                                margin-right: 0.5rem;
                                color: white;
                            }
                        }
                    }
                }
            }
        }

        @media(max-width: '767px') {
            .details-wrap {
                .purchase-summary {
                    .amount-lines {
                        // padding: 0.5rem 0;
                    }
                }
            }
        }

        .billing-total .bignumber,
        .details-wrap .purchase-summary .price {
            letter-spacing: -1px;
        }
        .no-discounts { opacity: 0; }
        &.upgrade, &.downgrade {
            h3,
            .billing-total .bignumber,
            .details-wrap .purchase-summary .price { color: white; }
            .text-primary { color: white !important; }
            .text-unassigned { color: white !important; opacity: 0.5; }
            .text-info { color: white !important; }
            .text-favorite { color: $colorYellow !important; }
            .subtitle, .subtitle-text { color: white !important; }
            .billing-total { background: #d9ede924; }
            .price--discount { color: $colorBrandHighlight !important; }
        }
        .billing-total {
            display: flex;
            flex-direction: column;
            padding: 0.5rem 1rem;
            background: #f2f2f2;
            border-bottom-left-radius: $radiusLg;
            border-bottom-right-radius: $radiusLg;
            overflow: hidden;
            .amount-wrap {
                display: flex;
                align-items: center;
                justify-content: space-between;
                .subtitle {
                    font-size: 14px;
                }
                .bignumber {
                    font-size: 32px;
                    font-family: DINNextLTPro-Medium, sans-serif;
                }
            }
        }
    }

</style>